'use client';

import moment from 'moment-timezone';
import type React from 'react';
import { Badge } from 'reactstrap';
import { wrapStringWithLink } from 'src/helpers/message-utils';
import { createAbbreviation } from 'src/helpers/utils';
import type { TenantContactMessage } from 'src/types';

interface TextBubbleProps {
	message: TenantContactMessage;
	tenantName: string;
	jobTimezone: string;
}

// Alignment derives from direction — NOT sender.
// outbound => Lula (right, green avatar); inbound => Tenant (left, initials avatar).
const TextBubble: React.FC<TextBubbleProps> = ({
	message,
	tenantName,
	jobTimezone,
}) => {
	const isOutbound = message.direction === 'outbound';
	const dir = message.direction;
	const time =
		message.created_date_utc && jobTimezone
			? moment
					.utc(message.created_date_utc, 'YYYY-MM-DD HH:mm:ss')
					.tz(jobTimezone)
					.format('h:mmA z')
			: '';
	const initials = createAbbreviation(tenantName) || 'T';

	return (
		<div className={`tenant-texts__row tenant-texts__row--${dir}`}>
			<div
				className={`tenant-texts__avatar tenant-texts__avatar--${dir}`}
				aria-hidden="true"
			>
				{/* Decorative avatar glyph only (aria-hidden). The Lula logo is
				    intentional chrome, NOT synthesized data — the BE-supplied
				    sender is rendered in the sender line below. */}
				{isOutbound ? (
					<svg
						width="18"
						height="9"
						viewBox="0 0 18 9"
						fill="none"
						xmlns="http://www.w3.org/2000/svg"
					>
						<path
							d="M10.2913 0.782757C10.2922 0.567073 10.3834 0.363144 10.5387 0.226629L10.5885 0.182888C10.8661 -0.0610434 11.2668 -0.0541639 11.537 0.19917C11.7451 0.394263 11.8668 0.67401 11.8675 0.970806C11.8685 1.44496 11.8386 2.25732 11.8047 3.29526C11.7492 4.99132 11.6961 5.7528 11.6961 6.41792C11.6961 6.57211 11.7067 6.71688 11.7253 6.85214C11.8422 7.70453 12.8115 7.64754 12.8725 6.78844C12.8807 6.67349 12.8814 6.55487 12.8724 6.43377C12.7089 4.23885 14.3446 2.29321 16.0207 3.51071C16.0697 3.54629 16.1379 3.53128 16.1742 3.48113C16.4821 3.05608 17.1168 3.05354 17.3549 3.45936C17.4562 3.63216 17.386 3.87899 17.3398 4.19877C17.1772 5.32419 17.1362 5.79601 17.0733 6.5093C17.0104 7.2226 17.0483 7.60489 17.3456 7.60489C17.6428 7.60489 17.645 7.22509 17.8765 7.32734C18.0905 7.42189 17.9867 7.81027 17.8629 8.19694C17.739 8.58362 17.4826 8.97044 16.9581 8.97044C16.5832 8.97044 16.335 8.47789 16.224 8.19684C16.1961 8.12631 16.0996 8.11748 16.0649 8.18446C15.5629 9.15551 14.306 9.27128 13.6576 8.40621L13.5275 8.23264C13.4936 8.18745 13.428 8.19315 13.4013 8.24361C12.9099 9.17311 11.6922 9.25354 11.0986 8.39569L10.9869 8.23422C10.9538 8.18635 10.8872 8.18602 10.8536 8.23358L10.8145 8.28911C10.1405 9.24515 9.01904 9.22442 8.37088 8.24797C8.33485 8.19369 8.25992 8.19294 8.2225 8.24612C7.52662 9.23513 6.14176 9.24901 5.437 8.26729C5.39375 8.20705 5.30404 8.21315 5.26202 8.27439C5.10017 8.51024 4.69405 8.97044 3.98906 8.97044C3.04117 8.97044 3.12075 8.63706 2.55492 8.63706C1.9891 8.63706 1.83205 8.97044 0.927854 8.97044C-0.107233 8.97044 -0.0650863 7.98183 0.0702096 6.9924C0.30394 5.0174 0.255115 2.85445 0.178461 0.833696C0.161557 0.589668 0.257454 0.351123 0.437205 0.200985L0.4788 0.166243C0.759942 -0.068582 1.15721 -0.0531051 1.4218 0.202982C1.62137 0.396134 1.73822 0.668822 1.7418 0.957471C1.76485 2.81324 1.69092 5.74655 1.59649 6.54473C1.50205 7.3429 1.68686 7.55327 2.22236 7.3429C2.75787 7.13254 3.18879 7.12983 3.76197 7.3429C4.33514 7.55598 4.76071 7.36837 4.76071 6.41792C4.76071 6.00906 4.77387 5.52201 4.75516 4.99132C4.77446 4.59641 4.74426 4.19997 4.71409 3.92836C4.68993 3.71086 4.75685 3.48988 4.90536 3.33991C5.18037 3.06218 5.61069 3.063 5.88729 3.3389C6.08163 3.53276 6.19526 3.80541 6.18402 4.09016C6.17435 4.33488 6.15466 4.65617 6.11469 4.99132C6.09608 5.51911 6.05022 6.00398 6.05022 6.41792C6.05022 6.62789 6.08733 6.82784 6.14543 7.01223C6.3752 7.74151 7.28495 7.72905 7.50531 6.99641C7.55969 6.81563 7.59426 6.62089 7.59426 6.41792C7.59426 6.00906 7.60742 5.52201 7.58871 4.99132C7.60858 4.58466 7.57597 4.17639 7.54495 3.90447C7.52175 3.70108 7.58195 3.49439 7.71772 3.35056C7.98276 3.0698 8.41211 3.07106 8.68128 3.34725C8.86428 3.53503 8.97195 3.79748 8.9611 4.06919C8.95119 4.31708 8.93091 4.64791 8.88944 4.99132C8.87193 5.47461 8.82755 5.49856 8.82755 6.54473C8.82755 6.55872 8.8277 6.57276 8.828 6.58686C8.85374 7.79623 10.2771 7.79219 10.3031 6.58282C10.3033 6.57006 10.3035 6.55736 10.3035 6.54473C10.3035 5.42348 10.3213 4.55294 10.3213 3.29526C10.3213 2.03758 10.2877 1.62594 10.2913 0.782757ZM14.1149 6.03476C14.1149 7.03884 14.4636 7.4584 15.0144 7.4584C15.5651 7.4584 15.7684 6.81481 15.8404 5.90307C15.9125 4.99132 15.6569 4.42102 15.1429 4.42102C14.6289 4.42102 14.1149 5.03068 14.1149 6.03476Z"
							fill="white"
						/>
					</svg>
				) : (
					initials
				)}
			</div>
			<div className={`tenant-texts__content tenant-texts__content--${dir}`}>
				<span className="tenant-texts__sender">
					{message.sender ?? ''}
					{isOutbound && message.designation ? (
						<Badge
							color="none"
							className="tenant-texts__badge"
							aria-hidden="true"
						>
							{message.designation}
						</Badge>
					) : null}
					{!isOutbound ? (
						<Badge
							color="none"
							className="tenant-texts__badge"
							aria-hidden="true"
						>
							Tenant
						</Badge>
					) : null}
				</span>
				<div className={`tenant-texts__bubble tenant-texts__bubble--${dir}`}>
					<p className="tenant-texts__text">
						{wrapStringWithLink({ message: message.text }) || ''}
					</p>
				</div>
				{time ? <span className="tenant-texts__time">{time}</span> : null}
			</div>
		</div>
	);
};

export default TextBubble;
